import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  THEME_DARK,
  THEME_LIGHT,
  THEME_STORAGE_KEY,
  isThemeValue,
} from "../theme/themeConstants";
import { applyTheme, resolveTheme, syncNativeStatusBar } from "../theme/applyTheme";

const ThemeContext = createContext(null);

function readStoredTheme() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeValue(stored) ? stored : THEME_LIGHT;
  } catch {
    return THEME_LIGHT;
  }
}

function persistTheme(theme) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Ignore quota / private-mode failures
  }
}

const FALLBACK_THEME = {
  preference: THEME_LIGHT,
  theme: THEME_LIGHT,
  isDark: false,
  setTheme: () => {},
  toggleTheme: () => {},
};

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readStoredTheme());
  const isDark = theme === THEME_DARK;

  useEffect(() => {
    applyTheme(theme);
    syncNativeStatusBar();
  }, [theme]);

  const setTheme = useCallback((next) => {
    const resolved = resolveTheme(next);
    setThemeState(resolved);
    persistTheme(resolved);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === THEME_DARK ? THEME_LIGHT : THEME_DARK);
  }, [setTheme, theme]);

  const value = useMemo(
    () => ({
      preference: theme,
      theme,
      isDark,
      setTheme,
      toggleTheme,
    }),
    [theme, isDark, setTheme, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext) || FALLBACK_THEME;
}

export default ThemeContext;
